import React from 'react';

import { defaultLocale, TCurrency } from '@/config/locales';
import { PriceType } from '@/redux/selectors/listing/page';
import { IPrice } from '@/utility/mapSearchResultToTile';

import PriceCurrent from './PriceCurrent';
import PriceInstantBook from './PriceInstantBook';

type IPriceElement = React.HTMLAttributes<HTMLElement>;

export enum PriceSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export interface IProps extends IPrice {
  isCampground?: boolean;
  currency?: TCurrency;
  showDiscount?: boolean;
  showOriginalPrice?: boolean;
  size?: PriceSize;
  testId?: string;
  isMapPopup?: boolean;
}

const Price: React.FC<IProps & IPriceElement> = ({
  isCampground,
  size = PriceSize.medium,
  currency = defaultLocale.base_currency,
  discount,
  instantBook = false,
  originalPrice,
  currentPrice,
  estimatedPriceBreakdown,
  overrideDayPrice = 0,
  discountFromOverrideDayPrice = 0,
  showDiscount = isCampground ? discountFromOverrideDayPrice > 0 : discount > 0,
  showOriginalPrice = isCampground ? currentPrice < overrideDayPrice : currentPrice < originalPrice,
  testId = 'price',
  period: pricePeriod,
  isMapPopup,
}) => {
  // Calculate hasDiscount using the ACTUAL prices that will be displayed
  const actualCurrentPrice = estimatedPriceBreakdown?.total ?? currentPrice;
  const actualOriginalPrice =
    estimatedPriceBreakdown?.original_total ?? (isCampground ? overrideDayPrice : originalPrice);
  const hasDiscount = isCampground
    ? actualCurrentPrice < overrideDayPrice
    : actualCurrentPrice < actualOriginalPrice;

  // When estimatedPriceBreakdown exists, derive period from it.
  const period = (() => {
    if (estimatedPriceBreakdown) {
      return estimatedPriceBreakdown?.use_day_pricing ? PriceType.DAY : PriceType.NIGHT;
    }

    return pricePeriod;
  })();

  return (
    <div
      className={`flex text-gray-900 items-baseline mt-1 ${
        showOriginalPrice ? 'flex-col ' : ''
      }lg:flex-row`}
      data-testid={testId}>
      <div className="flex items-baseline lg:contents">
        {instantBook && (
          <PriceInstantBook className="self-center" size={size} testId={`${testId}-instant-book`} />
        )}
        <PriceCurrent
          size={size}
          currency={currency}
          currentPrice={actualCurrentPrice}
          originalPrice={actualOriginalPrice}
          period={period}
          testId={`${testId}-current`}
          isMapPopup={isMapPopup}
          duration={estimatedPriceBreakdown?.duration}
          originalTotalPrice={estimatedPriceBreakdown?.original_total}
          showDiscount={showDiscount}
          hasDiscount={hasDiscount}
        />
      </div>
    </div>
  );
};

export default Price;
